import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/hooks/use-toast';
import { Package, ArrowRight, Palette, Check, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { PlanSelection } from '@/components/onboarding/PlanSelection';
import { ManagementModeSelection } from '@/components/onboarding/ManagementModeSelection';
import { usePlanAccess, UserPlan } from '@/hooks/usePlanAccess';

interface OnboardingData {
  fullName: string;
  companyName: string;
  theme: 'light' | 'dark';
  selectedPlan: UserPlan;
}

export const OnboardingFlow = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);  
  const [invitedMode, setInvitedMode] = useState(false);
  const [showModeSelection, setShowModeSelection] = useState(false);
  const { updatePlan } = usePlanAccess();
  const [data, setData] = useState<OnboardingData>({
    fullName: user?.full_name || '',
    companyName: '',
    theme: theme,
    selectedPlan: null,
  });

  // If the user already has a company_id, consider it as invited mode and lock the company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (user?.company_id) {
        setInvitedMode(true);
        const { data: comp } = await supabase
          .from('companies')
          .select('name')
          .eq('id', user.company_id)
          .single();
        if (comp?.name) {
          setData(prev => ({ ...prev, companyName: comp.name }));
        }
      }
    };
    fetchCompanyName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id]);

  const totalSteps = invitedMode ? 4 : 5;
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 2:
        if (invitedMode) {
          return data.fullName.trim() !== '';
        }
        return data.fullName.trim() !== '' && data.companyName.trim() !== '';
      case 3:
        return data.selectedPlan !== null;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      // Si étape 3 (sélection plan) et plan Premium, afficher la sélection de mode
      if (currentStep === 3 && data.selectedPlan === 'premium') {
        setShowModeSelection(true);
        return;
      }
      
      // Si étape 3 et plan Stats, sauter la sélection de mode et finaliser
      if (currentStep === 3 && data.selectedPlan === 'stats') {
        await completeOnboarding();
        return;
      }
      
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      let message = '';
      if (currentStep === 2) {
        message = invitedMode
          ? 'Veuillez renseigner votre nom complet'
          : 'Veuillez remplir tous les champs obligatoires';
      } else if (currentStep === 3) {
        message = 'Veuillez sélectionner un plan';
      }
      
      toast({
        title: "Validation requise",
        description: message,
        variant: "destructive"
      });
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const completeOnboarding = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Mise à jour du profil avec le nom et le plan
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: data.fullName,
          subscription_plan: data.selectedPlan,
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      let companyId = user.company_id;

      // Si l'utilisateur n'est pas invité (pas de company_id), créer une entreprise
      if (!user.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .rpc('create_company_with_owner', {
            company_name: data.companyName,
            company_description: null
          });

        if (companyError) throw companyError;
        companyId = companyData;
      }

      // Initialiser/mettre à jour l'abonnement
      const { error: subError } = await supabase.functions.invoke('check-subscription');
      if (subError) throw subError;

      toast({
        title: "Configuration terminée !",
        description: invitedMode
          ? "Votre compte a été configuré et rattaché à l'entreprise."
          : "Votre entreprise a été créée et votre essai démo de 7 jours a commencé",
        variant: "default"
      });

      // Rediriger selon le plan
      if (data.selectedPlan === 'stats') {
        navigate('/statistics');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erreur lors de la finalisation:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la configuration",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-gradient-primary rounded-2xl flex items-center justify-center mb-8 shadow-elegant">
              <Package className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Bienvenue dans Chart-Flow !
              </h2>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                {invitedMode
                  ? "Finalisons votre compte pour rejoindre l'entreprise qui vous a invité."
                  : "Nous allons configurer votre compte en quelques étapes simples."}
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Informations de base
              </h2>
              <p className="text-muted-foreground">
                {invitedMode
                  ? "Confirmez votre nom et vérifiez l'entreprise de rattachement"
                  : "Parlez-nous de vous et de votre entreprise"}
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nom complet *</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Votre nom complet"
                  value={data.fullName}
                  onChange={(e) => setData(prev => ({ ...prev, fullName: e.target.value }))}
                  className="transition-all duration-200 focus:shadow-md"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="companyName">
                    Nom de l'entreprise {invitedMode ? '(verrouillé)' : '*'}
                  </Label>
                  {invitedMode && (
                    <span className="inline-flex items-center text-xs text-muted-foreground gap-1">
                      <Lock className="w-3 h-3" /> Défini par l'invitant
                    </span>
                  )}
                </div>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Nom de votre entreprise"
                  value={data.companyName}
                  onChange={(e) => setData(prev => ({ ...prev, companyName: e.target.value }))}
                  className="transition-all duration-200 focus:shadow-md"
                  disabled={invitedMode}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        // Étape sélection du plan
        return (
          <PlanSelection
            selectedPlan={data.selectedPlan}
            onPlanSelected={(plan) => {
              setData(prev => ({ ...prev, selectedPlan: plan }));
              // Réinitialiser showModeSelection si l'utilisateur change de plan
              setShowModeSelection(false);
            }}
          />
        );

      case 4:
        // Étape personnalisation
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-xl flex items-center justify-center mb-4">
                <Palette className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Personnalisation
              </h2>
              <p className="text-muted-foreground">
                Configurez l'apparence selon vos préférences
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Thème de l'interface</Label>
                <div className="flex gap-4 justify-center">
                  <button
                    type="button"
                    onClick={() => theme === 'dark' && toggleTheme()}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      theme === 'light' 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <div className="w-16 h-12 bg-white rounded-lg border mb-3 relative overflow-hidden">
                      <div className="absolute top-1 left-1 right-1 h-2 bg-gray-100 rounded"></div>
                      <div className="absolute bottom-1 left-1 right-1 h-6 bg-gray-50 rounded"></div>
                    </div>
                    <span className="text-sm font-medium">Clair</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => theme === 'light' && toggleTheme()}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      theme === 'dark' 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <div className="w-16 h-12 bg-gray-900 rounded-lg border mb-3 relative overflow-hidden">
                      <div className="absolute top-1 left-1 right-1 h-2 bg-gray-800 rounded"></div>
                      <div className="absolute bottom-1 left-1 right-1 h-6 bg-gray-700 rounded"></div>
                    </div>
                    <span className="text-sm font-medium">Sombre</span>
                  </button>
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center space-x-2 text-success">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Configuration presque terminée !</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Plus qu'une étape
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        // Étape finale
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-xl flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Prêt à commencer !
              </h2>
              <p className="text-muted-foreground">
                Votre compte est configuré et prêt à l'emploi
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Si on doit afficher la sélection du mode (Premium uniquement)
  if (showModeSelection) {
    return <ManagementModeSelection />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-4">
      <div className="w-full max-w-2xl">
        <Card className="shadow-card border-0">
          <CardHeader className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Étape {currentStep} sur {totalSteps}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardHeader>
          
          <CardContent className="space-y-8">
            {renderStep()}
            
            <div className="flex justify-between space-x-4">
              {currentStep > 1 ? (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Retour
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  onClick={handleNext}
                  disabled={!validateStep(currentStep)}
                  className="flex-1 bg-gradient-primary hover:shadow-elegant transition-all duration-300"
                >
                  {currentStep === 1 ? 'Commencer' : 'Suivant'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={completeOnboarding}
                  disabled={isLoading}
                  className="flex-1 bg-gradient-primary hover:shadow-elegant transition-all duration-300"
                >
                  {isLoading ? 'Configuration...' : 'Terminer et accéder au dashboard'}
                  <Check className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};